<template>
  <b-sidebar 
    id="add-new-plant-sidebar" 
    :visible="isAddNewPlantSidebarActive" 
    bg-variant="white"
    sidebar-class="sidebar-lg" 
    shadow 
    backdrop 
    no-header 
    right 
    @hidden="resetForm"
    @change="(val) => $emit('update:is-add-new-plant-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <h5 class="mb-0">
          {{ $t('plant.addNew') }}
        </h5>
        <feather-icon 
          class="ml-1 cursor-pointer" 
          icon="XIcon" 
          size="16" 
          @click="hide" 
        />
      </div>

      <!-- BODY -->
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <!-- Form -->
        <b-form 
          class="p-2" 
          @submit.prevent="handleSubmit(onSubmit)" 
          @reset.prevent="resetForm"
        >
          <!-- Name -->
          <validation-provider 
            #default="validationContext" 
            name="Name" 
            rules="required"
          >
            <b-form-group 
              :label="$t('plant.name')" 
              label-for="name"
            >
              <b-form-input
                id="name" 
                v-model="plantData.name" 
                :state="getValidationState(validationContext)"
                trim 
                autofocus
                :placeholder="$t('plant.namePlaceholder')"
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Address -->
          <validation-provider 
            #default="validationContext" 
            name="Address" 
            rules="required"
          >
            <b-form-group 
              :label="$t('plant.address')" 
              label-for="address"
            >
              <b-form-textarea
                id="address" 
                v-model="plantData.address" 
                :state="getValidationState(validationContext)"
                trim 
                rows="3"
                :placeholder="$t('plant.addressPlaceholder')"
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Longitude -->
          <validation-provider 
            #default="validationContext" 
            name="Longitude" 
            rules="required"
          >
            <b-form-group 
              :label="$t('plant.longitude')" 
              label-for="longitude"
            >
              <b-form-input 
                id="longitude" 
                v-model="plantData.longitude" 
                :state="getValidationState(validationContext)"
                type="number"
                step="any"
                :placeholder="$t('plant.longitudePlaceholder')"
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Latitude -->
          <validation-provider 
            #default="validationContext" 
            name="Latitude" 
            rules="required"
          >
            <b-form-group 
              :label="$t('plant.latitude')" 
              label-for="latitude"
            >
              <b-form-input 
                id="latitude" 
                v-model="plantData.latitude" 
                :state="getValidationState(validationContext)"
                type="number"
                step="any"
                :placeholder="$t('plant.latitudePlaceholder')"
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button 
              v-ripple.400="'rgba(255, 255, 255, 0.15)'" 
              variant="primary" 
              class="mr-2" 
              type="submit"
            >
              {{ $t('Add') }}
            </b-button>
            <b-button 
              v-ripple.400="'rgba(186, 191, 199, 0.15)'" 
              type="button" 
              variant="outline-secondary"
              @click="hide"
            >
              {{ $t('Cancel') }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { ref } from '@vue/composition-api'
import { required } from '@validations'
import formValidation from '@core/comp-functions/forms/form-validation'
import Ripple from 'vue-ripple-directive'
import { getUserData } from '@/auth/utils'
import store from '@/store'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent'
import {
  VBModal, BCard, BRow, BCol, BFormInput, BButton, BTable, BMedia, BAvatar, BLink,
  BBadge, BDropdown, BDropdownItem, BPagination, BFormGroup, BFormSelect, BSidebar, 
  BForm, BFormInvalidFeedback, BFormTextarea
} from 'bootstrap-vue'

export default {
  name: 'PlantListAddNew',
  components: {
    VBModal, BCard, BRow, BCol, BFormInput, BButton, BTable, BMedia, BAvatar, BLink,
    BBadge, BDropdown, BDropdownItem, BPagination, BFormGroup, BFormSelect, BSidebar, 
    BForm, BFormInvalidFeedback, BFormTextarea,
    ValidationProvider,
    ValidationObserver,
    ToastificationContent
  },
  directives: {
    Ripple,
  },
  model: {
    prop: 'isAddNewPlantSidebarActive',
    event: 'update:is-add-new-plant-sidebar-active',
  },
  props: {
    isAddNewPlantSidebarActive: {
      type: Boolean,
      required: true,
    }
  },
  data() {
    return {
      required
    }
  },
  setup(props, { emit }) {
    const toast = useToast()
    const blankPlantData = {
      name: '',
      address: '',
      longitude: '',
      latitude: '',
    }

    const plantData = ref(JSON.parse(JSON.stringify(blankPlantData)))
    const resetPlantData = () => {
      plantData.value = JSON.parse(JSON.stringify(blankPlantData))
    }

    const onSubmit = () => {
      plantData.value.clientGuid = getUserData().setting.clientguid
      store.dispatch('createPlant', {
        ...plantData.value
      })
        .then(() => {
          emit('refetch-data')
          emit('update:is-add-new-plant-sidebar-active', false)
          toast({
            component: ToastificationContent,
            props: {
              title: 'Success adding plant',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Error adding plant',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }

    const {
      refFormObserver,
      getValidationState,
      resetForm,
    } = formValidation(resetPlantData)

    return {
      plantData,
      onSubmit,
      refFormObserver,
      getValidationState,
      resetForm,
    }
  },
}
</script>

<style lang="scss">
#add-new-plant-sidebar {
  .vs__dropdown-menu {
    max-height: 200px !important;
  }
}
</style>

